import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Switch from "react-switch";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError } from "../../../core/utils";
import close from "../../../assets/Images/icons/Cancel.svg";
import styled from "styled-components";

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormField = styled.div`
    margin-bottom: 16px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
    color: #333;
`;

const SwitchContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
`;

const CountryCheckboxContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin-top: 8px;
`;

const ErrorText = styled.div`
    color: red;
    font-size: 12px;
    margin-top: 4px;
`;

const PasswordInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    input {
        padding-right: 40px;
    }

    .eye-icon {
        position: absolute;
        right: 10px;
        cursor: pointer;
        z-index: 1;
    }
`;

// Vertical options for dropdown
const VERTICAL_OPTIONS = [
    { value: "sell_direct", label: "Sell my car (Direct Offer)" },
    { value: "sell_brokerage", label: "Sell my car (Brokerage)" },
    { value: "buy", label: "Buy a car" },
    { value: "insurance", label: "Car insurance" },
    { value: "finance", label: "Car Financing" },
];

const AddBroker = ({ onSuccess, brokerId = null, isEdit = false, onClose }) => {
    const [open, setOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showApiSecret, setShowApiSecret] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [countries, setCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [brokerData, setBrokerData] = useState(null);

    useEffect(() => {
        const countriesData = JSON.parse(localStorage.getItem("countries") || "[]");
        setCountries(countriesData);
    }, []);

    // Fetch broker data when in edit mode
    useEffect(() => {
        if (brokerId && isEdit) {
            setFetching(true);
            API.get(`${APP_URLS.BROKER_MANAGEMENT}/${brokerId}`)
                .then((res) => {
                    const resp = res.data;
                    if (resp.success === true) {
                        setBrokerData(resp.data.item);
                        setOpen(true);
                    } else {
                        toast.error(resp.message || "Failed to fetch broker data", {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                    }
                })
                .catch(function (error) {
                    const error_message = error?.response ? APIError(error) : error?.message || "Failed to fetch broker data";
                    toast.error(error_message, {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                })
                .finally(() => {
                    setFetching(false);
                });
        }
    }, [brokerId, isEdit]);

    // Create validation schema based on edit mode
    const validationSchema = Yup.object().shape({
        broker_name: Yup.string().required("Broker Name is required"),
        status: Yup.boolean(),
        countries: Yup.array()
            .min(1, "At least one country must be selected")
            .required("Country selection is required"),
        vertical: isEdit
            ? Yup.string()
            : Yup.string().required("Vertical is required"),
        api_url: Yup.string()
            .required("API URL is required")
            .url("API URL must be a valid URL"),
        api_endpoint: Yup.string().required("API Endpoint is required"),
        api_key: Yup.string().required("API Key is required"),
        api_secret: Yup.string().required("API Secret is required"),
    });

    // Initialize form values based on mode
    const getInitialValues = () => {
        if (isEdit && brokerData) {
            // In edit mode, include editable fields
            return {
                broker_name: brokerData.name || brokerData.broker_name || brokerData.company_name || "",
                status: brokerData.is_active === 1 || brokerData.is_active === true || brokerData.status === true,
                countries: brokerData.countries || brokerData.country_ids || [],
                api_url: brokerData.api_url || "",
                api_endpoint: brokerData.api_endpoint || "",
                api_key: brokerData.api_key || "",
                api_secret: brokerData.api_secret || "",
            };
        }
        // In add mode, include all fields
        return {
            broker_name: "",
            status: true,
            countries: [],
            vertical: "",
            api_url: "",
            api_endpoint: "",
            api_key: "",
            api_secret: "",
        };
    };

    // Compute initial values reactively based on brokerData
    const initialValues = getInitialValues();

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // In edit mode, send editable fields
                const data = isEdit ? {
                    name: values.broker_name,
                    is_active: values.status ? 1 : 0,
                    country_ids: selectedCountries,
                    api_url: values.api_url,
                    api_endpoint: values.api_endpoint,
                    api_key: values.api_key,
                    api_secret: values.api_secret,
                } : {
                    // In add mode, send all fields
                    name: values.broker_name,
                    is_active: values.status ? 1 : 0,
                    country_ids: selectedCountries,
                    api_url: values.api_url,
                    api_endpoint: values.api_endpoint,
                    api_key: values.api_key,
                    api_secret: values.api_secret,
                    vertical: values.vertical,
                };

                const url = isEdit && brokerId
                    ? `${APP_URLS.UPDATE_BROKER}/${brokerId}?_method=patch`
                    : APP_URLS.BROKER_MANAGEMENT;

                const method = isEdit && brokerData ? 'post' : 'post';
                const response = await API[method](url, data);
                const resp = response.data;

                if (resp.success === true) {
                    toast.success(isEdit ? "Broker updated successfully." : "Broker added successfully.", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                    handleClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                } else {
                    toast.error(resp.message, {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            } catch (error) {
                const errorMessage = error?.response ? APIError(error) : error?.message || (isEdit ? "Failed to update broker" : "Failed to add broker");
                toast.error(errorMessage, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClose = () => {
        setOpen(false);
        formik.resetForm();
        setSelectedCountries([]);
        setShowPassword(false);
        setShowApiSecret(false);
        setBrokerData(null);
        if (onClose) {
            onClose();
        }
    };

    const handleCountryChange = (countryId) => {
        const isUnselecting = selectedCountries.includes(countryId);
        const newSelected = isUnselecting
            ? selectedCountries.filter((id) => id !== countryId)
            : [...selectedCountries, countryId];

        setSelectedCountries(newSelected);
        formik.setFieldValue("countries", newSelected);

        // Only mark as touched when unselecting results in empty array
        if (isUnselecting && newSelected.length === 0) {
            formik.setFieldTouched("countries", true);
        } else if (newSelected.length > 0) {
            // Clear error if we have at least one country selected
            formik.setFieldTouched("countries", false);
        }
    };

    // Initialize selected countries when in edit mode or when formik reinitializes
    useEffect(() => {
        if (isEdit && brokerData) {
            const countryIds = brokerData.country_ids || brokerData.countries || [];
            setSelectedCountries(countryIds);
        } else if (!isEdit) {
            setSelectedCountries([]);
        }
    }, [isEdit, brokerData]);

    // Sync selectedCountries with formik values when formik reinitializes
    useEffect(() => {
        if (formik.values.countries && formik.values.countries.length > 0) {
            setSelectedCountries(formik.values.countries);
        }
    }, [formik.values.countries]);

    // Filter countries to show only France and Belgium (or all if not found)
    const availableCountries = countries.filter(
        (country) =>
            country.fullname?.toLowerCase().includes("france") ||
            country.fullname?.toLowerCase().includes("belgium")
    );

    const displayCountries = availableCountries.length > 0 ? availableCountries : countries.slice(0, 2);

    // Don't render button in edit mode
    if (isEdit && !open && !fetching) {
        return null;
    }

    // Show loading state while fetching broker data
    if (isEdit && fetching) {
        return null;
    }

    return (
        <>
            {!isEdit && (
                <button className="btn" onClick={() => setOpen(true)}>
                    + Add Broker
                </button>
            )}
            {open && (
                <div className="modal fade show custom-modal modal-backdrop">
                    <div className="modal">
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{isEdit ? "Edit Broker" : "Add New Broker"}</h5>
                                    <img
                                        src={close}
                                        alt="close"
                                        className="mx-2"
                                        role="button"
                                        onClick={handleClose}
                                    />
                                </div>
                                <form onSubmit={formik.handleSubmit}>
                                    <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                        {/* Row 1: Broker Name and Status */}
                                        <FormRow>
                                            <FormField>
                                                <Label>Broker Name</Label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.broker_name && formik.errors.broker_name ? "is-invalid" : ""}`}
                                                    name="broker_name"
                                                    value={formik.values.broker_name || ""}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {formik.touched.broker_name && formik.errors.broker_name && (
                                                    <ErrorText>{formik.errors.broker_name}</ErrorText>
                                                )}
                                            </FormField>
                                            <FormField>
                                                <Label>Status</Label>
                                                <SwitchContainer>
                                                    <Switch
                                                        onChange={(checked) => formik.setFieldValue("status", checked)}
                                                        checked={formik.values.status}
                                                        className="react-switch"
                                                        uncheckedIcon={false}
                                                        checkedIcon={false}
                                                        offColor="#D9D9D9"
                                                        onColor="#7589EC"
                                                    />
                                                    <span style={{ color: formik.values.status ? "#7589EC" : "#666" }}>
                                                        {formik.values.status ? "Active" : "Inactive"}
                                                    </span>
                                                </SwitchContainer>
                                            </FormField>
                                        </FormRow>

                                        {/* Row 2: Country Selection */}
                                        <FormField>
                                            <Label>Country Selection</Label>
                                            <CountryCheckboxContainer>
                                                {displayCountries.map((country) => (
                                                    <div key={country.id} className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id={`country-${country.id}`}
                                                            checked={selectedCountries.includes(country.id)}
                                                            onChange={() => handleCountryChange(country.id)}
                                                            className="me-2"
                                                        />
                                                        <label htmlFor={`country-${country.id}`} style={{ margin: 0, cursor: "pointer" }}>
                                                            {country.fullname || country.name}
                                                        </label>
                                                    </div>
                                                ))}
                                            </CountryCheckboxContainer>
                                            {formik.touched.countries && formik.errors.countries && (
                                                <ErrorText>{formik.errors.countries}</ErrorText>
                                            )}
                                        </FormField>

                                        {/* Row 3: Vertical Selection - Only show in add mode */}
                                        {!isEdit && (
                                            <FormField>
                                                <Label>Vertical</Label>
                                                <select
                                                    className={`form-control ${formik.touched.vertical && formik.errors.vertical ? "is-invalid" : ""}`}
                                                    name="vertical"
                                                    value={formik.values.vertical}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                >
                                                    <option value="">Select Vertical</option>
                                                    {VERTICAL_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {formik.touched.vertical && formik.errors.vertical && (
                                                    <ErrorText>{formik.errors.vertical}</ErrorText>
                                                )}
                                            </FormField>
                                        )}

                                        {/* Row 4: API URL */}
                                        <FormField>
                                            <Label>API URL</Label>
                                            <input
                                                type="text"
                                                className={`form-control ${formik.touched.api_url && formik.errors.api_url ? "is-invalid" : ""}`}
                                                name="api_url"
                                                value={formik.values.api_url}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="https://api.example.com"
                                            />
                                            {formik.touched.api_url && formik.errors.api_url && (
                                                <ErrorText>{formik.errors.api_url}</ErrorText>
                                            )}
                                        </FormField>

                                        {/* Row 5: API Endpoint */}
                                        <FormField>
                                            <Label>API Endpoint</Label>
                                            <input
                                                type="text"
                                                className={`form-control ${formik.touched.api_endpoint && formik.errors.api_endpoint ? "is-invalid" : ""}`}
                                                name="api_endpoint"
                                                value={formik.values.api_endpoint}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            {formik.touched.api_endpoint && formik.errors.api_endpoint && (
                                                <ErrorText>{formik.errors.api_endpoint}</ErrorText>
                                            )}
                                        </FormField>

                                        {/* Row 6: API Key with show/hide */}
                                        <FormField>
                                            <Label>API Key</Label>
                                            <PasswordInputWrapper>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    className={`form-control ${formik.touched.api_key && formik.errors.api_key ? "is-invalid" : ""}`}
                                                    name="api_key"
                                                    value={formik.values.api_key}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                <IconButton
                                                    className="eye-icon"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    size="small"
                                                    style={{ padding: "4px" }}
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </PasswordInputWrapper>
                                            {formik.touched.api_key && formik.errors.api_key && (
                                                <ErrorText>{formik.errors.api_key}</ErrorText>
                                            )}
                                        </FormField>

                                        {/* Row 7: API Secret with show/hide */}
                                        <FormField>
                                            <Label>API Secret</Label>
                                            <PasswordInputWrapper>
                                                <input
                                                    type={showApiSecret ? "text" : "password"}
                                                    className={`form-control ${formik.touched.api_secret && formik.errors.api_secret ? "is-invalid" : ""}`}
                                                    name="api_secret"
                                                    value={formik.values.api_secret}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                <IconButton
                                                    className="eye-icon"
                                                    onClick={() => setShowApiSecret(!showApiSecret)}
                                                    size="small"
                                                    style={{ padding: "4px" }}
                                                >
                                                    {showApiSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </PasswordInputWrapper>
                                            {formik.touched.api_secret && formik.errors.api_secret && (
                                                <ErrorText>{formik.errors.api_secret}</ErrorText>
                                            )}
                                        </FormField>
                                    </div>
                                    <div className="d-flex justify-content-end p-3">
                                        <button
                                            type="button"
                                            className="btn cancel-btn btn-border"
                                            onClick={handleClose}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn ms-3"
                                            disabled={loading}
                                        >
                                            {loading ? (isEdit ? "Updating..." : "Adding...") : (isEdit ? "Update Broker" : "Add Broker")}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddBroker;
